import { AxiosResponse } from "axios";
import api from "./api";
import {
  IK8sServiceList,
  IMaintenanceHandlersResponse,
  IPagerDutyRule,
  IPagerDutyRuleUpdate,
  IPagerDutyRulesResponse,
  IPipelineStatus,
  IPipelineSummary,
  IRuleCacheStats,
  IStartServiceRequest,
} from "../types/api/pipeline.type";

// Served by this app's own nginx (pipelines-proxy.conf.template) and always
// same-origin (SAME_ORIGIN_ENDPOINTS in api.ts). Not a SERVICE_BASE_URLS root:
// it is not part of the public API and has no location in api-proxy.conf.template.
const ROOT = "/pipelines-api";

const TENANT_NAME_ANNOTATION = "controlrooms.ai/tenant-name";
const TENANT_ID_ANNOTATION = "controlrooms.ai/tenant-id";

/**
 * Without the PIPELINES_NAMESPACE routes, nginx falls through to the SPA and
 * answers /pipelines-api/... with index.html and a 200 — which would otherwise
 * be read as an API response. Only that HTML fallback is an error here: any
 * other body (including an empty one, which axios leaves as "") is a real
 * pipeline response, and the action it answers has already run.
 */
const isSpaFallback = (response: AxiosResponse<unknown>) => {
  const contentType = String(response.headers?.["content-type"] ?? "");
  if (contentType.includes("text/html")) return true;
  return typeof response.data === "string" && /^\s*<(!doctype|html)/i.test(response.data);
};

const ensureJson = <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
  if (isSpaFallback(response)) {
    throw new Error(
      "Pipelines routes are not configured on this deployment (PIPELINES_NAMESPACE is not set)."
    );
  }
  return response;
};

const pipelinePath = (envId: string, path: string) =>
  `${ROOT}/${encodeURIComponent(envId)}${path}`;

const listPipelines = async (): Promise<IPipelineSummary[]> => {
  const { data } = ensureJson(await api.get<IK8sServiceList>(`${ROOT}/registry`));
  return (data.items ?? [])
    .map((svc) => {
      const envId = svc.metadata.labels?.tenant ?? svc.metadata.name.replace(/^pipeline-/, "");
      const annotations = svc.metadata.annotations ?? {};
      return {
        envId,
        tenantName: annotations[TENANT_NAME_ANNOTATION] || envId,
        tenantId: annotations[TENANT_ID_ANNOTATION],
        serviceName: svc.metadata.name,
      };
    })
    .filter((p) => /^\d+$/.test(p.envId))
    .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
};

const getStatus = async (envId: string) =>
  ensureJson(await api.get<IPipelineStatus>(pipelinePath(envId, "/status")));

const startService = async (envId: string, service: string, body: IStartServiceRequest) =>
  ensureJson(await api.post(pipelinePath(envId, `/${service}/start`), body));

const stopService = async (envId: string, service: string) =>
  ensureJson(await api.post(pipelinePath(envId, `/${service}/stop`), {}));

const scaleService = async (envId: string, service: string, workers: number) =>
  ensureJson(await api.post(pipelinePath(envId, `/${service}/scale`), { workers }));

const getMaintenanceHandlers = async (envId: string) =>
  ensureJson(await api.get<IMaintenanceHandlersResponse>(pipelinePath(envId, "/maintenance/handlers")));

const startMaintenanceHandler = async (envId: string, name: string) =>
  ensureJson(
    await api.post(pipelinePath(envId, `/maintenance/handlers/${encodeURIComponent(name)}/start`), {})
  );

const stopMaintenanceHandler = async (envId: string, name: string) =>
  ensureJson(
    await api.post(pipelinePath(envId, `/maintenance/handlers/${encodeURIComponent(name)}/stop`), {})
  );

const getRuleCacheStats = async (envId: string) =>
  ensureJson(await api.get<IRuleCacheStats>(pipelinePath(envId, "/alert/rules/cache/stats")));

const invalidateRuleCache = async (envId: string) =>
  ensureJson(await api.post(pipelinePath(envId, "/alert/rules/cache/invalidate"), {}));

const getPagerDutyRules = async (envId: string) =>
  ensureJson(await api.get<IPagerDutyRulesResponse>(pipelinePath(envId, "/pagerduty/rules")));

const updatePagerDutyRule = async (
  envId: string,
  service: string,
  condition: string,
  update: IPagerDutyRuleUpdate
) =>
  ensureJson(
    await api.post<IPagerDutyRule>(
      pipelinePath(envId, `/pagerduty/rules/${service}/${condition}`),
      update
    )
  );

/**
 * Human-readable message from a rejected call. api.ts rejects with the response
 * body when there is one — the pipeline API's errors are FastAPI's {detail}.
 */
export const pipelineErrorMessage = (e: unknown, fallback = "Request failed"): string => {
  const err = e as { detail?: unknown; message?: unknown } | undefined;
  if (err && typeof err.detail === "string") return err.detail;
  if (err && Array.isArray(err.detail)) {
    return err.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join("; ") || fallback;
  }
  if (err && typeof err.message === "string" && err.message) return err.message;
  if (typeof e === "string" && e && !e.trimStart().startsWith("<")) return e;
  return fallback;
};

const PipelineService = {
  listPipelines,
  getStatus,
  startService,
  stopService,
  scaleService,
  getMaintenanceHandlers,
  startMaintenanceHandler,
  stopMaintenanceHandler,
  getRuleCacheStats,
  invalidateRuleCache,
  getPagerDutyRules,
  updatePagerDutyRule,
};

export default PipelineService;
